<template>
    <div>
        <v-card class="write-off-form" outlined>
            <v-card-title class="page-heading primary">
                <v-row align="center">
                    <v-col class="d-flex align-center" cols="12" sm="9">
                        <v-btn dark icon @click="goBack">
                            <v-icon>fal fa-arrow-left</v-icon>
                        </v-btn>
                        <div class="ml-3">{{ $t('write_offs.heading') }}</div>
                    </v-col>

                    <v-col cols="12" sm="3" class="d-flex justify-end">
                        <v-btn
                            v-if="!disabled && !form.recorded_at"
                            dark
                            outlined
                            :disabled="!form.items.length"
                            @click="save(false)"
                        >
                            <v-icon left>fal fa-check</v-icon>
                            {{ $t('write_offs.save') }}
                        </v-btn>

                        <v-btn
                            v-if="!disabled && !form.recorded_at"
                            class="mx-3"
                            dark
                            outlined
                            :disabled="!form.items.length"
                            @click="record"
                        >
                            <v-icon left>fal fa-file-check</v-icon>
                            {{ $t('write_offs.record') }}
                        </v-btn>

                        <v-btn
                            v-if="form.recorded_at"
                            class="mx-3"
                            dark
                            outlined
                            :disabled="!form.items.length"
                            @click="rollback"
                        >
                            <v-icon left>fal fa-file-times</v-icon>
                            {{ $t('write_offs.rollback') }}
                        </v-btn>

                        <v-menu bottom transition="slide-y-transition">
                            <template v-slot:activator="{ on }">
                                <v-btn dark v-on="on" icon>
                                    <v-icon>far fa-ellipsis-v</v-icon>
                                </v-btn>
                            </template>

                            <v-list>
                                <v-list-item
                                    key="print"
                                    :disabled="!writeOff || !writeOff.recorded_at"
                                    @click="print"
                                >
                                    <v-list-item-action class="mx-3">
                                        <v-icon :disabled="!writeOff || !writeOff.recorded_at">
                                            fal fa-print
                                        </v-icon>
                                    </v-list-item-action>
                                    <v-list-item-title>
                                        {{ $t('general.print') }}
                                    </v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    </v-col>
                </v-row>
            </v-card-title>

            <v-card-text>
                <v-row class="mt-3 mx-0">
                    <v-col cols="12" md="4">
                        <div class="highlight-section">
                            <div class="section-title">
                                <v-icon class="mr-3">fal fa-file-alt</v-icon>
                                {{ $t('write_offs.section_document_info') }}
                            </div>

                            <div class="section-content">
                                <v-row>
                                    <v-col cols="6">
                                        <v-menu
                                            v-model="picker1"
                                            :close-on-content-click="false"
                                            :nudge-right="32"
                                            transition="scale-transition"
                                            min-width="290"
                                            offset-y
                                        >
                                            <template v-slot:activator="{ on }">
                                                <v-text-field
                                                    v-model="form.date"
                                                    :error-messages="form.$errors.date"
                                                    :label="$t('general.date')"
                                                    prepend-icon="fal fa-calendar"
                                                    dense
                                                    readonly
                                                    v-on="on"
                                                    :disabled="disabled || !!form.items.length"
                                                    @blur="form.validate('date')"
                                                    @input="delete form.$errors.date"
                                                />
                                            </template>
                                            <v-date-picker
                                                v-model="form.date"
                                                @input="picker1 = false; form.validate('date')"
                                            />
                                        </v-menu>
                                    </v-col>

                                    <v-col cols="6">
                                        <v-text-field
                                            v-model="form.number"
                                            :label="$t('write_offs.number')"
                                            dense
                                            :disabled="disabled"
                                            :error-messages="form.$errors.number"
                                            @blur="form.validate('number')"
                                            @input="delete form.$errors.number"
                                        />
                                    </v-col>
                                </v-row>

                                <v-row>
                                    <v-col cols="12">
                                        <v-textarea
                                            v-model="form.description"
                                            class="ma-0"
                                            :label="$t('general.description')"
                                            dense
                                            auto-grow
                                            :rows="1"
                                            :disabled="disabled"
                                            :error-messages="form.$errors.description"
                                            @blur="form.validate('description')"
                                            @input="delete form.$errors.description"
                                        />
                                    </v-col>
                                </v-row>
                            </div>
                        </div>
                    </v-col>

                    <v-col cols="12" md="4">
                        <div class="highlight-section">
                            <div class="section-title mb-4">
                                <v-icon class="mr-3">fal fa-sigma</v-icon>
                                {{ $t('write_offs.section_summary') }}
                            </div>

                            <div class="section-content">
                                <div class="d-flex justify-space-between">
                                    <span>{{ $t('write_offs.selling_value') }}:</span>
                                    <span>{{ writeOffInternal.getSellingValue().toFormat('0.00') }}</span>
                                </div>
                                <div class="d-flex justify-space-between">
                                    <span>{{ $t('write_offs.tax') }}:</span>
                                    <span>{{ writeOffInternal.getTax().toFormat('0.00') }}</span>
                                </div>
                                <div class="d-flex justify-space-between">
                                    <span>{{ $t('write_offs.selling_value_after_tax') }}:</span>
                                    <span>{{ writeOffInternal.getSellingValueAfterTax().toFormat('0.00') }}</span>
                                </div>
                            </div>
                        </div>
                    </v-col>
                </v-row>

                <v-data-table
                    :headers="headers"
                    :items="form.items"
                    :footer-props="{ 'items-per-page-options': [10, 20, 50, 100] }"
                    :mobile-breakpoint="0"
                    class="items-table operations striped"
                    dense
                >
                    <template v-slot:header.actions="{ on, props }">
                        <div v-if="!disabled" class="d-flex justify-end">
                            <v-btn icon large color="primary" @click="openItemForm()">
                                <v-icon>fal fa-plus</v-icon>
                            </v-btn>
                        </div>
                    </template>

                    <template v-slot:item="{ item, index }">
                        <tr>
                            <td>{{ index + 1 }}</td>
                            <td>{{ item.getProduct() && item.getProduct().code ? item.getProduct().code : '' }}</td>
                            <td>{{ item.getProduct() ? item.getProduct().name : '' }}</td>
                            <td>{{ item.getQuantity() }} <small>{{ item.getUnitOfMeasure() }}</small></td>
                            <td class="primary--text font-weight-bold">{{ item.getSellingPrice().toFormat('0.00') }}</td>
                            <td class="primary--text font-weight-bold">{{ item.getSellingPriceAfterTax().toFormat('0.00') }}</td>
                            <td class="primary--text font-weight-bold">{{ item.getSellingValue().toFormat('0.00') }}</td>
                            <td>{{ item.getTax().toFormat('0.00') }}</td>
                            <td class="primary--text font-weight-bold">{{ item.getSellingValueAfterTax().toFormat('0.00') }}</td>

                            <td align="center" class="w35 maxw60">
                                <v-menu
                                    v-if="!disabled"
                                    bottom
                                    transition="slide-y-transition"
                                >
                                    <template v-slot:activator="{ on }">
                                        <v-btn v-on="on" icon>
                                            <v-icon>far fa-ellipsis-h</v-icon>
                                        </v-btn>
                                    </template>

                                    <v-list>
                                        <v-list-item :key="1" @click="openItemForm(item)">
                                            <v-list-item-action class="mr-4 flex justify-content-center">
                                                <v-icon>fal fa-pen</v-icon>
                                            </v-list-item-action>
                                            <v-list-item-title>{{ $t('general.edit') }}</v-list-item-title>
                                        </v-list-item>

                                        <v-divider />

                                        <v-list-item :key="2" @click="deleteItem(item)">
                                            <v-list-item-action class="mr-4 flex justify-content-center">
                                                <v-icon class="error--text">fal fa-times</v-icon>
                                            </v-list-item-action>
                                            <v-list-item-title class="error--text">
                                                {{ $t('general.delete') }}
                                            </v-list-item-title>
                                        </v-list-item>
                                    </v-list>
                                </v-menu>
                            </td>
                        </tr>
                    </template>
                </v-data-table>
            </v-card-text>
        </v-card>

        <!-- Item form -->
        <write-off-item-edit
            :show.sync="showItemForm"
            :item="selectedItem"
            :products="products"
            :pricelists="pricelists"
            :date="form.date"
            @save="onItemSave"
        />
    </div>
</template>

<script>
import { loadData } from '@/views/shared/loadPageData';
import ApiService from '@/services/ApiService';
import Product from '@/models/Product';
import WriteOff from '@/models/WriteOff';
import WriteOffItemEdit from '@/views/shared/admin/writeOffs/WriteOffItemEdit';
import { required } from 'vuelidate/lib/validators';
import HtmlGen from '@/wrappers/HtmlGen';

export default {
    components: {
        WriteOffItemEdit
    },

    async created() {
        let url = '/admin/load/write-off' + (this.$route.params.id ? '/' + this.$route.params.id : '');

        const data = await loadData(url);

        if(data.writeOff) {
            this.writeOff = new WriteOff(data.writeOff);
        }

        this.nextDocumentNumber = data.maxDocumentNumber ? parseInt(data.maxDocumentNumber) + 1 : 1;
        this.products = data.products.map(p => new Product(p));
        this.pricelists = data.pricelists;
        this.form = this.createForm();
    },

    data() {
        return {
            writeOff: null,
            nextDocumentNumber: null,
            products: [],
            pricelists: [],
            form: this.createForm(),
            showItemForm: false,
            headers: [
                { text: this.$t('general.number'), value: 'product.number', sortable: false },
                { text: this.$t('general.code'), value: 'product.code', sortable: false },
                { text: this.$t('general.inventory_item'), value: 'name', sortable: false },
                { text: this.$t('write_offs.quantity'), value: 'quantity', sortable: false },
                { text: this.$t('write_offs.selling_price'), value: 'selling_price', sortable: false },
                { text: this.$t('write_offs.selling_price_after_tax'), value: 'selling_price_after_tax', sortable: false },
                { text: this.$t('write_offs.selling_value'), value: 'selling_value', sortable: false },
                { text: this.$t('write_offs.tax'), value: 'tax', sortable: false },
                { text: this.$t('write_offs.selling_value_after_tax'), value: 'selling_value_after_tax', sortable: false },
                { text: '', value: 'actions', sortable: false }
            ],

            selectedItem: null,
            picker1: false,
            picker2: false,
            writeOffNumberPrefix: moment().format('YY/')
        };
    },

    validations: {
        form: {
            number: { required },
            date: { required },
        }
    },

    computed: {
        writeOffInternal() {
            return new WriteOff(this.form.toJson());
        },

        disabled() {
            return !!this.form.recorded_at;
        }
    },

    methods: {
        createForm() {
            return new Form(this, 'form', {
                id: this.writeOff?.id || null,
                user_id: this.user?.id || null,
                number: this.writeOff?.number || this.writeOffNumberPrefix + this.nextDocumentNumber,
                date: this.writeOff?.date
                    ? moment(this.writeOff.date).format('YYYY-MM-DD')
                    : moment().format('YYYY-MM-DD'),
                description: this.writeOff?.description || null,
                items: this.writeOff?.items || [],
                recorded_at: this.writeOff?.recorded_at || null,
            });
        },

        openItemForm(item = null) {
            if(!this.form.validate()) return;

            this.selectedItem = item;
            this.showItemForm = true;
        },

        deleteItem(item) {
            this.form.items = this.form.items.filter(i => i !== item);
            this.showItemForm = false;
        },

        onItemSave(item) {
            let isEdit = this.form.items.some(i => i.hash === item.hash);
            if(!isEdit) {
                this.form.items.push(item);
                return;
            }

            this.form.items.forEach((i, index, array) => {
                if (i.hash === item.hash) {
                    this.$set(array, index, item);
                }
            });
        },

        async save(recordPending = false) {
            if(!this.form.validate()) return;

            if (!this.form.id) {
                const response = await ApiService.post('/admin/write-offs', this.form);
                this.writeOff = new WriteOff(response.data);
                this.form = this.createForm();

                if(recordPending) return;

                this.$snackbar(this.$t('write_offs.saved'), 'success');
            } else {
                const response = await ApiService.patch(`/admin/write-offs/${this.form.id}`, this.form);
                this.writeOff = new WriteOff(response.data);
                this.form = this.createForm();

                if(recordPending) return;

                this.$snackbar(this.$t('write_offs.updated'), 'success');
            }

            this.goBack();
        },

        async record() {
            await this.save(true);

            const response = await ApiService.post(`/admin/write-offs/${this.form.id}/record`, {})
            if(response.data.products?.length) {
                Product.insert({ data: response.data.products, freeze: true });
            }

            this.$snackbar(this.$t('write_offs.recorded'), 'success');

            this.goBack();
        },

        async rollback() {
            if(!this.form.id) return;

            const response = await ApiService.post(`/admin/write-offs/${this.form.id}/rollback`, {})
            if(response.data.products?.length) {
                Product.insert({ data: response.data.products, freeze: true });
            }

            this.$snackbar(this.$t('write_offs.rolled_back'), 'success');

            this.goBack();
        },

        print() {
            const html = this.writeOff.toTextLines().map(line => HtmlGen.fromTextLine(line)).join('');
            this.$printService.printCustom(null, html, null, { choosePrinter: true })
        },

        goBack() {
            this.$router.push({ name: 'adminWriteOffs' }).catch(err => {});
        },
    }
};
</script>

<style lang="scss" scoped>
.write-off-form {
    .page-heading {
        padding: 12px 16px;
        color: white;
        font-size: 16px;
        word-break: break-word;
    }

    .highlight-section {
        border-radius: 3px;
        background: #fafafa;
        border: 1px solid #cccccc;
        padding: 20px;
        min-height: 215px;
        font-size: 15px;

        .section-title {
            margin-bottom: 24px;
            font-size: 20px;
            color: black;

            .v-icon {
                color: black;
            }
        }
    }

    ::v-deep .items-table {
        margin-top: 28px;

        th {
            font-size: 11px;
            padding: 0 4px;
            max-width: 80px;

            span {
                white-space: break-spaces;
            }
        }

        td {
            font-size: 13px;
            padding: 0 4px;
        }

        .separator {
            border-left: 1px solid rgba(0, 0, 0, 0.5);
            padding-left: 12px;
        }
    }
}
</style>
